/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { Component, OnInit, Input } from "@angular/core";
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';
import { Observable, Observer } from 'rxjs';

import { InstanceData } from '../../models/instance-data';
import { Property } from '../../models/property';
import { PropertyType } from '../../models/property-type';
import { LocationInstance } from '../../models/location-instance';
import { ObjectInstance } from '../../models/object-instance';
import { TagInstance } from '../../models/tag-instance';
import { ZoneInstance } from '../../models/zone-instance';
import { User } from '../../models/user';

import { EntityService } from "../../services/entity.service";
import { GroupService } from "../../services/group.service";
import { LocationService } from "../../services/location.service";
import { ObjectService } from "../../services/object.service";
import { TagService } from "../../services/tag.service";
import { UserService } from "../../services/user.service";
import { ZoneService } from "../../services/zone.service";
import { switchMap } from "rxjs/operators";

@Component({
  selector: 'app-entity-field',
  templateUrl: './entity-field.component.html'
})
export class EntityFieldComponent implements OnInit {

    @Input()
    datum: InstanceData;

    typeaheadValue: string;
    typeaheadLoading: boolean;
    typeaheadNoResults: boolean;
    entities: Observable<any>;

    constructor(private groupService: GroupService,
        private entityService: EntityService,
        private locationService: LocationService,
        private objectService: ObjectService,
        private tagService: TagService,
        private userService: UserService,
        private zoneService: ZoneService) {}


    ngOnInit(): void {
        //KHT 08/17/2021 - updated to new ngx-bootstrap standard
        this.entities = new Observable((observer: Observer<string>) => {
            observer.next(this.typeaheadValue);
        }).pipe(
            switchMap((query: string) => {
                if(query){
                    return this.search(query);
                }
            })
        )

        if (this.datum && this.datum.value) {
            this.loadReferenceValue();
        }
    }

    getFieldLabel(): string {
        if (this.datum != null && this.datum.property != null) {
            return this.datum.property.name;
        }
        else {
            return "?";
        }
    }

    async loadReferenceValue(): Promise<void> {
        if (this.datum && this.datum.property) {
            switch (this.datum.property.type) {
                case PropertyType.OBJECT_REFERENCE:
                    const object: ObjectInstance = await this.objectService.getObjectInstanceForId(Number(this.datum.value));
                    if (object != null) {
                        this.typeaheadValue = object.name;
                    }
                    
                    break;
                case PropertyType.LOCATION_REFERENCE:
                    const location: LocationInstance = await this.locationService.getLocationInstanceById(Number(this.datum.value));
                    if (location != null) {
                        this.typeaheadValue = location.name;
                    }
    
                    break;
                case PropertyType.TAG_REFERENCE:
                    const tag: TagInstance = await this.tagService.getTagInstanceById(Number(this.datum.value));
                    if (tag != null) {
                        this.typeaheadValue = tag.name;
                    }
    
                    break;
                case PropertyType.USER_REFERENCE:
                    const user: User = await this.userService.getUserForId(Number(this.datum.value));
                    if (user != null) {
                        this.typeaheadValue = user.firstName + " " + user.lastName;
                    }
                    
                    break;
                case PropertyType.ZONE_REFERENCE:
                    const zone: ZoneInstance = await this.zoneService.getZoneInstanceById(Number(this.datum.value));
                    if (zone != null) {
                        this.typeaheadValue = zone.name;
                    }
    
                    break;
            }
        }
    }
    
    changeTypeaheadLoading(e: boolean): void {
        this.typeaheadLoading = e;
    }
    
    typeaheadOnSelect(e: TypeaheadMatch) {
        console.log('Selected value: ', e.item);
        this.datum.value = e.item.id;
    }
    
    search(token: string) {
        if (this.datum.property.type == PropertyType.USER_REFERENCE) {
            return this.entityService.searchUser(token);
        }
        else {
            return this.entityService.search(this.datum.property.type, token);
        }
    }

}
